import type { ResolvedVisitShipping } from '../utils/resolve-visit-shipping-addresses';
import { useMonitoringIntegration } from '../context/monitoringIntegration.context';
import { VisitStatus, VisitStatusColors, VisitStatusLabels } from '../enums';
import { MyLocation as MyLocationIcon } from '@mui/icons-material';
import type { RouteMonitoringVisitShipping } from '../models';
import PackagesDetailBadge from './PackagesDetailBadge';
import { formatVisitCardTime } from '../utils';
import { IconButton } from '@mui/material';
import { Link } from 'react-router-dom';

interface VisitCardProps {
  routeStopOrder: number | 'base';
  visitedAt: string | null;
  scheduledFrom: string;
  status: VisitStatus;
  address: {
    streetName: string | null;
    doorNumber: string | null;
    apartmentNumber: string | null;
  } | null;
  shippingNumber: number;
  shippings?: RouteMonitoringVisitShipping[];
  shippingEntries?: ResolvedVisitShipping[];
  externalId?: string | null;
  packagesCount?: number;
  packagesPicked?: { departure: number; arrival: number };
  onRequestMapFocus?: () => void;
}

const formatApartment = (apartmentNumber: string): string =>
  apartmentNumber.includes('Apto') || apartmentNumber.includes('Ap')
    ? apartmentNumber
    : `Apto ${apartmentNumber}`;

const VisitCard = ({
  routeStopOrder,
  visitedAt,
  scheduledFrom,
  status,
  address,
  shippingNumber,
  shippings,
  shippingEntries,
  externalId,
  packagesCount,
  packagesPicked,
  onRequestMapFocus,
}: VisitCardProps) => {
  const { shippingBasePath } = useMonitoringIntegration();
  const colors = VisitStatusColors[status];
  const label = VisitStatusLabels[status];
  const time = formatVisitCardTime(visitedAt, scheduledFrom);
  const externalIdTrimmed = externalId?.trim() ?? '';

  const shippingNumbers: number[] =
    shippings && shippings.length > 0
      ? shippings.flatMap((s) => (s.number != null && s.number > 0 ? [s.number] : []))
      : shippingNumber > 0
        ? [shippingNumber]
        : [];

  const externalIdLine =
    externalIdTrimmed !== '' ? (
      <div className='text-sm text-gray-500'>
        ID externo: {externalIdTrimmed}
      </div>
    ) : null;

  const shippingLink = (num: number, isPickup: boolean) => (
    <Link
      to={`${shippingBasePath}/${num}`}
      target='_blank'
      rel='noopener noreferrer'
      onClick={(event) => event.stopPropagation()}
      className='relative z-10 text-sm text-blue-600 hover:text-blue-800 hover:underline inline-block focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 rounded'
    >
      Ver envío — ID: {num}
      {isPickup ? ' (Retiro)' : ''}
    </Link>
  );

  const shippingIds = (
    <>
      {shippingNumbers.length > 0 ? (
        <div className='flex flex-col gap-0.5 mt-1'>
          {shippingNumbers.map((num) => (
            <span key={num}>{shippingLink(num, false)}</span>
          ))}
        </div>
      ) : shippingNumber > 0 ? (
        <div className='text-sm text-gray-500 mt-1'>ID: {shippingNumber}</div>
      ) : null}
      {externalIdLine}
    </>
  );

  const hasPerShippingAddresses =
    shippingEntries != null && shippingEntries.length > 0;

  const perShippingContent = hasPerShippingAddresses ? (
    <div className='flex flex-col gap-3'>
      {shippingEntries!.map((entry) => (
        <div
          key={entry.number}
          className='text-base font-medium text-gray-800 leading-tight'
        >
          {entry.address?.streetName ? (
            <div>
              {entry.address.streetName}
              {entry.address.doorNumber && ` ${entry.address.doorNumber}`}
            </div>
          ) : (
            <div className='text-gray-500'>Sin dirección</div>
          )}
          {entry.address?.apartmentNumber && (
            <div className='text-gray-600'>
              {formatApartment(entry.address.apartmentNumber)}
            </div>
          )}
          <div className='mt-0.5 flex flex-wrap items-center gap-2'>
            {shippingLink(entry.number, entry.isPickup)}
            {entry.statusLabel && (
              <span className='rounded-full bg-sky-100 px-2 py-0.5 text-xs font-semibold text-sky-800'>
                {entry.statusLabel}
              </span>
            )}
            {entry.serviceLabel && (
              <span className='rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700'>
                {entry.serviceLabel}
              </span>
            )}
          </div>
        </div>
      ))}
      {externalIdLine}
    </div>
  ) : null;

  const orderLabel = routeStopOrder === 'base' ? 'Base' : `#${routeStopOrder}`;

  return (
    <div className='relative z-10 bg-white rounded-lg shadow-md flex items-center gap-3 overflow-hidden'>
      <div
        className='w-1 h-14 flex-shrink-0 self-center'
        style={{ backgroundColor: colors.ballColor || colors.textColor }}
      />
      <div className='flex shrink-0 flex-col items-center justify-center px-1 py-3 min-w-[3.25rem]'>
        <span
          className={`text-center font-black tabular-nums leading-none tracking-tight ${
            routeStopOrder === 'base'
              ? 'text-sm text-gray-500'
              : 'text-xl text-[var(--primary-color)]'
          }`}
          title={
            routeStopOrder === 'base'
              ? 'Parada base / origen'
              : 'Orden en el mapa'
          }
        >
          {orderLabel}
        </span>
      </div>
      <div className='flex items-center gap-4 flex-1 p-4'>
        <div className='text-lg font-semibold text-gray-800 min-w-[80px]'>
          {time}
        </div>

        {packagesCount != null && packagesCount > 0 && (
          <PackagesDetailBadge
            packagesCount={packagesCount}
            packagesPicked={packagesPicked}
          />
        )}

        <div
          className='px-3 py-1 rounded flex items-center gap-2 flex-shrink-0'
          style={{ backgroundColor: colors.backgroundColor }}
        >
          <div
            className='w-2 h-2 rounded-full'
            style={{ backgroundColor: colors.ballColor || colors.textColor }}
          />
          <span
            className='text-sm font-medium'
            style={{ color: colors.textColor || colors.ballColor }}
          >
            {label}
          </span>
        </div>

        <div className='flex min-w-0 flex-1 items-center gap-2'>
          <div className='min-w-0 flex-1'>
            {perShippingContent ?? (
              address ? (
                <div className='text-base font-medium text-gray-800 leading-tight'>
                  {address.streetName && (
                    <div>
                      {address.streetName}
                      {address.doorNumber && ` ${address.doorNumber}`}
                    </div>
                  )}
                  {address.apartmentNumber && (
                    <div className='text-gray-600'>
                      {formatApartment(address.apartmentNumber)}
                    </div>
                  )}
                  {shippingIds}
                </div>
              ) : (
                <div className='text-base font-medium text-gray-800'>
                  Sin dirección
                  {shippingIds}
                </div>
              )
            )}
          </div>
          {onRequestMapFocus != null ? (
            <IconButton
              size='small'
              aria-label='Ver en el mapa'
              title='Ver en el mapa'
              onClick={(event) => {
                event.stopPropagation();
                onRequestMapFocus();
              }}
              sx={{ color: '#1976d2' }}
            >
              <MyLocationIcon sx={{ fontSize: 22 }} />
            </IconButton>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default VisitCard;
